// PropertyLockManager.jsx
// Component for managing smart lock associations with properties

import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Stack,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { usePropertyLock, useAvailableLocksForProperty } from "../../hooks/usePropertyLocks";

/**
 * Component to manage smart lock for a specific property
 * @param {string} listingId - The property/listing ID
 * @param {object} propertyInfo - Property details (address, city, state)
 */
const PropertyLockManager = ({ listingId, propertyInfo }) => {
  const {
    locks,
    loading: lockLoading,
    error: lockError,
    associateLock,
    removeLock,
  } = usePropertyLock(listingId);

  const {
    availableLocks,
    loading: locksLoading,
    error: locksError,
    refetch: refetchAvailableLocks,
  } = useAvailableLocksForProperty(listingId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedLockId, setSelectedLockId] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const handleOpenDialog = () => {
    setDialogOpen(true);
    setActionError(null);
    refetchAvailableLocks();
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedLockId("");
    setActionError(null);
  };

  const handleAssociateLock = async () => {
    if (!selectedLockId) {
      setActionError("Please select a lock to associate");
      return;
    }

    setActionLoading(true);
    setActionError(null);
    try {
      await associateLock(selectedLockId);
      setSuccessMessage("Lock successfully associated with this property!");
      handleCloseDialog();
      refetchAvailableLocks(); // Refresh available locks list
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      setActionError(error.response?.data?.error || "Failed to associate lock");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveLock = async (lockId) => {
    if (!window.confirm("Are you sure you want to remove this lock from the property?")) {
      return;
    }

    setActionLoading(true);
    setActionError(null);
    try {
      await removeLock(lockId);
      setSuccessMessage("Lock removed from property successfully!");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      setActionError(error.response?.data?.error || "Failed to remove lock");
    } finally {
      setActionLoading(false);
    }
  };

  // Don't block rendering while loading - show skeleton instead

  return (
    <Box>
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {lockError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {lockError}
        </Alert>
      )}

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      )}

      <Card elevation={2}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" display="flex" alignItems="center" gap={1}>
              <LockIcon />
              Smart Lock Management {locks.length > 0 && `(${locks.length})`}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddCircleOutlineIcon />}
              onClick={handleOpenDialog}
              disabled={actionLoading || lockLoading}
            >
              Associate Lock
            </Button>
          </Stack>

          {lockLoading ? (
            <Box py={3}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading lock information...
                  </Typography>
                </Box>
              </Stack>
            </Box>
          ) : locks.length > 0 ? (
            <Stack spacing={2}>
              {locks.map((lock) => (
                <Card key={lock.lockId} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1}>
                      <Typography variant="subtitle1" fontWeight="600" mb={1}>
                        {lock.deviceName || "Unnamed Device"}
                      </Typography>

                      <Stack spacing={1}>
                        <Box display="flex" gap={1} alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            Type:
                          </Typography>
                          <Typography variant="body2">{lock.deviceType}</Typography>
                        </Box>

                        <Box display="flex" gap={1} alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            ID:
                          </Typography>
                          <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                            {lock.lockId}
                          </Typography>
                        </Box>

                        {lock.batteryLevel && (
                          <Box display="flex" gap={1} alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                              Battery:
                            </Typography>
                            <Chip
                              label={`${(lock.batteryLevel * 100).toFixed(0)}%`}
                              color={lock.batteryLevel > 0.3 ? "success" : "warning"}
                              size="small"
                            />
                          </Box>
                        )}

                        <Box display="flex" gap={1} alignItems="center">
                          <Chip
                            label="Associated"
                            color="success"
                            size="small"
                            icon={<LockIcon />}
                          />
                        </Box>
                      </Stack>
                    </Box>

                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => handleRemoveLock(lock.lockId)}
                      disabled={actionLoading}
                    >
                      Remove
                    </Button>
                  </Stack>
                </Card>
              ))}
            </Stack>
          ) : (
            <Box textAlign="center" py={3}>
              <LockOpenIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                No smart locks associated with this property
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                Click "Associate Lock" to add locks for automated access control
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Association Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Associate Smart Lock</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            {locksLoading ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress />
              </Box>
            ) : locksError ? (
              <Alert severity="error">{locksError}</Alert>
            ) : availableLocks.length === 0 ? (
              <Alert severity="info">
                No available locks found. Please connect a smart lock first.
              </Alert>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Select Lock</InputLabel>
                <Select
                  value={selectedLockId}
                  onChange={(e) => setSelectedLockId(e.target.value)}
                  label="Select Lock"
                >
                  {availableLocks.map((lock) => (
                    <MenuItem key={lock.lockId} value={lock.lockId}>
                      {lock.deviceName || "Unnamed Device"} ({lock.deviceType})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleAssociateLock}
            variant="contained"
            disabled={actionLoading || !selectedLockId}
          >
            {actionLoading ? <CircularProgress size={24} /> : "Associate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PropertyLockManager;

